import type {ElementType, ReactNode} from "react";
import {OtsActionButton} from "./OtsButton";
import {OtsDataTableScrollContext} from "./OtsDataTable";
import {OtsInlineAlert, type OtsInlineAlertTone} from "./OtsInlineAlert";
import {OtsPopover, type OtsPopoverProps, type OtsPopoverRenderApi} from "./OtsPopover";
import {OtsProgressBar} from "./OtsProgressBar";

export type OtsTraySlot = ReactNode | ((api: OtsPopoverRenderApi) => ReactNode);
export type OtsTrayProps = Pick<
  OtsPopoverProps,
  "trigger" | "onOpenChange" | "placement" | "brand" | "colorScheme" | "tooltip" | "className"
> & {
  /** Stable accessible name of the tray, independent of a changing trigger count. */
  label: string;
  /** Defaults to label; can include region or other context. */
  title?: ReactNode;
  /** Accessible trigger name, e.g. "3 Active Shifts". Defaults to label. */
  triggerLabel?: string;
  triggerAs?: ElementType;
  triggerClassName?: string;
  /** List, notice/detail, and table widths. All are constrained to the viewport. */
  size?: "sm" | "md" | "lg";
  /** Hide visual chrome for compact trays; the region keeps its accessible label. */
  showHeader?: boolean;
  /** Viewport constraints still apply when a smaller content height is requested. */
  maxHeight?: string;
  description?: ReactNode;
  actions?: OtsTraySlot;
  toolbar?: OtsTraySlot;
  children: OtsTraySlot;
  footer?: OtsTraySlot;
};

const widths = {sm: "w-[24rem]", md: "w-[32rem]", lg: "w-[56rem]"};
const slot = (content: OtsTraySlot, api: OtsPopoverRenderApi) =>
  typeof content === "function" ? content(api) : content;

/** Anchored, nonmodal tray chrome. Feature components own their content, state decisions and data. */
export function OtsTray({
  label,
  title,
  triggerLabel,
  triggerAs,
  triggerClassName,
  size = "sm",
  showHeader = true,
  maxHeight = "min(38rem,80dvh)",
  description,
  actions,
  toolbar,
  children,
  footer,
  placement = "bottom end",
  ...popover
}: OtsTrayProps) {
  return (
    <OtsPopover
      {...popover}
      ariaLabel={triggerLabel ?? label}
      buttonAs={triggerAs}
      buttonClassName={triggerClassName}
      placement={placement}
      maxHeight={maxHeight}
      viewportPadding={16}
      panelClassName={`${widths[size]} !max-w-[calc(100vw-2rem)] max-h-[inherit] overflow-hidden !p-0`}
    >
      {(api) => (
        // `!flex`: Mantine's unlayered reset makes sections `display: block`, which lets the body grow past the
        // tray instead of scrolling inside it.
        <section aria-label={label} className="!flex max-h-[inherit] min-h-0 min-w-0 flex-col">
          {showHeader && (
            <header className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--ots-outline-variant)] px-4 py-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-[var(--ots-text)]">{title ?? label}</h2>
                {description != null && <div className="mt-1 text-xs text-[var(--ots-text-muted)]">{description}</div>}
              </div>
              {actions != null && <div className="flex shrink-0 items-center gap-2">{slot(actions, api)}</div>}
            </header>
          )}
          {toolbar != null && (
            <div className="shrink-0 border-b border-[var(--ots-outline-variant)] px-4 py-3">{slot(toolbar, api)}</div>
          )}
          <div
            data-ots-tray-body
            role="group"
            aria-label={`${label} content`}
            tabIndex={0}
            className="min-h-0 min-w-0 flex-1 overflow-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--ots-primary)]"
          >
            <OtsDataTableScrollContext.Provider value={true}>{slot(children, api)}</OtsDataTableScrollContext.Provider>
          </div>
          {footer != null && (
            <footer className="shrink-0 border-t border-[var(--ots-outline-variant)] px-4 py-3 text-xs text-[var(--ots-text-muted)]">
              {slot(footer, api)}
            </footer>
          )}
        </section>
      )}
    </OtsPopover>
  );
}

/** Optional state displays; callers decide whether to replace content or retain cached rows. */
export function OtsTrayLoadingState({label}: {label: string}) {
  return (
    <div className="flex flex-col gap-2 px-4 py-6">
      <p role="status" className="text-xs text-[var(--ots-text-muted)]">
        {label}…
      </p>
      <OtsProgressBar size="compact" label={label} />
    </div>
  );
}

export function OtsTrayEmptyState({
  title,
  description,
  icon,
}: {
  title: ReactNode;
  /** Hide visual chrome for compact trays; the region keeps its accessible label. */
  showHeader?: boolean;
  /** Viewport constraints still apply when a smaller content height is requested. */
  maxHeight?: string;
  description?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-8 text-center">
      {icon != null && (
        <span aria-hidden="true" className="text-[var(--ots-text-muted)] [&>svg]:size-8">
          {icon}
        </span>
      )}
      <p className="text-sm font-semibold text-[var(--ots-text)]">{title}</p>
      {description != null && <p className="text-xs text-[var(--ots-text-muted)]">{description}</p>}
    </div>
  );
}

export function OtsTrayErrorState({
  title,
  children,
  tone = "danger",
  onRetry,
}: {
  title: ReactNode;
  children?: ReactNode;
  tone?: OtsInlineAlertTone;
  onRetry?: () => void;
}) {
  return (
    <div className="p-3">
      <OtsInlineAlert
        title={title}
        tone={tone}
        size="compact"
        action={
          onRetry && (
            <OtsActionButton size="compact" variant="secondary" onClick={onRetry}>
              Try again
            </OtsActionButton>
          )
        }
      >
        {children}
      </OtsInlineAlert>
    </div>
  );
}
